import React, { useEffect, useRef, useState } from 'react';

import AiBot from './AiBot';
import { saveServerLogs } from 'UtilityComponents/Util';

const REMOTE_NAME = 'corpMybizMyraLegacy';
const EXPOSED_MODULE = './myraBotLegacy';
const REMOTE_SCRIPT_ID = 'corp-mybiz-myra-remote-entry';
const REMOTE_MANIFEST_FILE = 'mf-manifest.json';
const REMOTE_ASSET_ATTR = 'data-myra-remote-asset';
const PROD_REMOTE_ENTRY = '//jsak.mmtcdn.com/mybiz-myra/assets/distMFLegacy/remoteLegacyEntry.js';
const DEV_REMOTE_ENTRY = 'http://127.0.0.1:3007/mybiz-myra/assets/distMFLegacy/remoteLegacyEntry.js';

let remoteContainerPromise;
let remoteManifestPromise;

const getRemoteEntryUrl = () => {
	if (typeof window === 'undefined') {
		return PROD_REMOTE_ENTRY;
	}

	if (window.__MYRA_REMOTE_ENTRY__) {
		return window.__MYRA_REMOTE_ENTRY__;
	}

	return typeof isDev !== 'undefined' && isDev ? DEV_REMOTE_ENTRY : PROD_REMOTE_ENTRY;
};

const getRemoteBaseUrl = (url) => url.replace(/remoteLegacyEntry\.js(?:\?.*)?$/, '');

const appendCacheKey = (url) => {
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}v=${window.remoteCacheKey || Date.now()}`;
};

const loadScriptByUrl = (url) => new Promise((resolve, reject) => {
	if (typeof window === 'undefined') {
		reject(new Error(`Cannot load remote script outside browser: ${url}`));
		return;
	}

	const existingScript = document.querySelector(`script[${REMOTE_ASSET_ATTR}="${url}"]`);
	if (existingScript) {
		if (existingScript.dataset.loaded === 'true') {
			resolve();
			return;
		}

		existingScript.addEventListener('load', () => resolve(), { once: true });
		existingScript.addEventListener('error', () => reject(new Error(`Failed to load remote script ${url}`)), { once: true });
		return;
	}

	const script = document.createElement('script');
	script.async = true;
	script.crossOrigin = 'anonymous';
	script.src = url;
	script.setAttribute(REMOTE_ASSET_ATTR, url);
	script.onload = () => {
		script.dataset.loaded = 'true';
		resolve();
	};
	script.onerror = () => reject(new Error(`Failed to load remote script ${url}`));
	document.body.appendChild(script);
});

const loadStyleByUrl = (url) => new Promise((resolve, reject) => {
	if (typeof window === 'undefined') {
		reject(new Error(`Cannot load remote style outside browser: ${url}`));
		return;
	}

	const existingLink = document.querySelector(`link[${REMOTE_ASSET_ATTR}="${url}"]`);
	if (existingLink) {
		resolve();
		return;
	}

	const link = document.createElement('link');
	link.rel = 'stylesheet';
	link.href = url;
	link.crossOrigin = 'anonymous';
	link.setAttribute(REMOTE_ASSET_ATTR, url);
	link.onload = () => resolve();
	link.onerror = () => reject(new Error(`Failed to load remote style ${url}`));
	document.head.appendChild(link);
});

const getRemoteManifest = async (remoteEntryUrl) => {
	if (!remoteManifestPromise) {
		const manifestUrl = appendCacheKey(`${getRemoteBaseUrl(remoteEntryUrl)}${REMOTE_MANIFEST_FILE}`);
		remoteManifestPromise = fetch(manifestUrl)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Failed to fetch Myra manifest from ${manifestUrl}`);
				}
				return response.json();
			})
			.catch((error) => {
				remoteManifestPromise = null;
				throw error;
			});
	}

	return remoteManifestPromise;
};

const preloadExposeAssets = async (remoteEntryUrl, exposedModule) => {
	const manifest = await getRemoteManifest(remoteEntryUrl);
	const expose = manifest && manifest.exposes && manifest.exposes.find((item) => item.path === exposedModule);

	if (!expose) {
		throw new Error(`Expose metadata missing for ${exposedModule}`);
	}

	const baseUrl = getRemoteBaseUrl(remoteEntryUrl);
	const jsAssets = [
		...(expose.assets?.js?.sync || []),
		...(expose.assets?.js?.async || [])
	];
	const cssAssets = [
		...(expose.assets?.css?.sync || []),
		...(expose.assets?.css?.async || [])
	];

	await Promise.all(cssAssets.map((asset) => loadStyleByUrl(`${baseUrl}${asset}`)));

	for (const asset of jsAssets) {
		// Load expose dependencies from the same origin as the remoteEntry to avoid prod publicPath mismatches.
		/* eslint-disable no-await-in-loop */
		await loadScriptByUrl(`${baseUrl}${asset}`);
		/* eslint-enable no-await-in-loop */
	}
};

const loadRemoteScript = () => new Promise((resolve, reject) => {
	if (typeof window === 'undefined') {
		reject(new Error('Myra remote can only load in the browser'));
		return;
	}

	if (window[REMOTE_NAME]) {
		resolve(window[REMOTE_NAME]);
		return;
	}

	const existingScript = document.getElementById(REMOTE_SCRIPT_ID);
	if (existingScript) {
		existingScript.addEventListener('load', () => resolve(window[REMOTE_NAME]), { once: true });
		existingScript.addEventListener('error', () => reject(new Error('Failed to load Myra remote entry')), { once: true });
		return;
	}

	const script = document.createElement('script');
	script.id = REMOTE_SCRIPT_ID;
	script.src = appendCacheKey(getRemoteEntryUrl());
	script.async = true;
	script.crossOrigin = 'anonymous';
	script.onload = () => resolve(window[REMOTE_NAME]);
	script.onerror = () => reject(new Error(`Failed to load Myra remote entry from ${script.src}`));
	document.body.appendChild(script);
});

const getRemoteContainer = async () => {
	if (!remoteContainerPromise) {
		remoteContainerPromise = loadRemoteScript().then(async (container) => {
			if (!container) {
				throw new Error(`Remote container ${REMOTE_NAME} was not found on window`);
			}

			return container;
		}).catch((error) => {
			remoteContainerPromise = null;
			throw error;
		});
	}

	return remoteContainerPromise;
};

const RemoteAiBot = () => {
	const mountRef = useRef(null);
	const cleanupRef = useRef(null);
	const [useFallback, setUseFallback] = useState(false);

	useEffect(() => {
		let isMounted = true;

		const mountRemoteBot = async () => {
			try {
				const container = await getRemoteContainer();
				await preloadExposeAssets(getRemoteEntryUrl(), EXPOSED_MODULE);
				const moduleFactory = await container.get(EXPOSED_MODULE);
				const remoteModule = moduleFactory();
				const mount = remoteModule && remoteModule.mount;

				if (typeof mount !== 'function' || !mountRef.current) {
					throw new Error(`Legacy mount API is missing for ${REMOTE_NAME}/${EXPOSED_MODULE}`);
				}

				if (!isMounted) {
					return;
				}

				cleanupRef.current = mount(mountRef.current);
			} catch (error) {
				saveServerLogs(error && error.stack ? error.stack : error, 'error', 'Remote Myra Bot');
				if (isMounted) {
					setUseFallback(true);
				}
			}
		};

		mountRemoteBot();

		return () => {
			isMounted = false;
			if (typeof cleanupRef.current === 'function') {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, []);

	if (useFallback) {
		return null;
	}

	return <div ref={mountRef} />;
};

export default RemoteAiBot;
